import { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { Profile, SubscriptionTier, STORAGE_LIMITS } from '../lib/supabase';
import { dbSelect, dbUpdate } from '../lib/api';

// Admin email that uses Max plan
const ADMIN_EMAIL = 'a.burkov@gmail.com';

// Supabase auth endpoints
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const projectRef = supabaseUrl?.split('//')[1]?.split('.')[0];
const storageKey = `sb-${projectRef}-auth-token`;

// Auth provider types
export type AuthProvider = 'email' | 'google' | 'unknown';

// Simplified User type (what we need from Supabase)
interface User {
  id: string;
  email?: string;
  user_metadata?: Record<string, unknown>;
  app_metadata?: Record<string, unknown>;
  aud?: string;
  created_at?: string;
}

// Simplified Session type
interface Session {
  access_token: string;
  refresh_token?: string;
  expires_at?: number;
  expires_in?: number;
  token_type?: string;
  user: User;
}

// Auth error type
interface AuthError {
  message: string;
  status?: number;
}

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  session: Session | null;
  loading: boolean;
  profileLoading: boolean;
  authReady: boolean;
  isAdmin: boolean;
  canUseClaudeCode: boolean;
  creatorFeaturesEnabled: boolean;
  isPasswordRecovery: boolean;
  subscriptionTier: SubscriptionTier;
  isPaidUser: boolean;
  isProUser: boolean;
  isPremiumUser: boolean;
  hasAds: boolean;
  subscriptionExpiresAt: Date | null;
  storageLimitBytes: number;
  signUp: (email: string, password: string, displayName?: string, country?: string) => Promise<{ error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signInWithGoogle: (redirectTo?: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  updateProfile: (updates: { display_name?: string | null; username?: string | null; anthropic_api_key?: string | null; kimi_api_key?: string | null; glm_api_key?: string | null; minimax_api_key?: string | null; auto_submit_errors?: boolean; is_public_profile?: boolean }) => Promise<{ error: Error | null }>;
  refreshProfile: () => Promise<void>;
  refreshAppSettings: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: AuthError | null }>;
  getAuthProvider: () => AuthProvider;
  isGoogleAccount: () => boolean;
  isEmailAccount: () => boolean;
  ensureFreshToken: (options?: { forceRefresh?: boolean }) => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper to save session to localStorage
function saveSession(session: Session) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(session));
  } catch (e) {
    console.error('Error saving session to localStorage:', e);
  }
}

// Helper to clear session from localStorage
function clearSession() {
  console.log('[AuthContext:clearSession] Clearing session, stack:', new Error().stack);
  try {
    localStorage.removeItem(storageKey);
  } catch (e) {
    console.error('Error clearing session from localStorage:', e);
  }
}

// Helper to get session from localStorage
function getStoredSession(): { user: User | null; session: Session | null } {
  try {
    const stored = localStorage.getItem(storageKey);
    console.log('[AuthContext:getStoredSession] stored exists:', !!stored, 'storageKey:', storageKey);
    
    if (stored) {
      const parsed = JSON.parse(stored);
      const sessionData = parsed.currentSession || parsed;
      const userData = sessionData?.user;
      const accessToken = sessionData?.access_token;
      
      console.log('[AuthContext:getStoredSession] sessionData:', {
        hasUser: !!userData,
        hasAccessToken: !!accessToken,
        expires_at: sessionData?.expires_at,
        provider: userData?.app_metadata?.provider,
        providers: userData?.app_metadata?.providers,
      });
      
      if (userData && accessToken) {
        return { 
          user: userData, 
          session: {
            access_token: accessToken,
            refresh_token: sessionData.refresh_token,
            user: userData,
            expires_at: sessionData.expires_at,
            expires_in: sessionData.expires_in,
            token_type: sessionData.token_type || 'bearer',
          }
        };
      }
    }
  } catch (e) {
    console.error('Error reading session from localStorage:', e);
  }
  return { user: null, session: null };
}

// Check if token is expired or about to expire (within 2 minutes)
function isTokenExpired(session: Session | null): boolean {
  if (!session?.expires_at) {
    console.log('[AuthContext:isTokenExpired] no expires_at, returning false');
    return false;
  }
  const expiresAt = session.expires_at * 1000;
  const now = Date.now();
  const expired = now >= expiresAt - 120000;
  console.log('[AuthContext:isTokenExpired] expiresAt:', new Date(expiresAt).toISOString(), 'now:', new Date(now).toISOString(), 'expired:', expired);
  // Refresh if expiring in the next 2 minutes (120000ms)
  return expired; 
}

function determineAuthProvider(user: User | null): AuthProvider {
  if (!user) return 'unknown';

  const providers = user.app_metadata?.providers as string[] | undefined;
  
  if (providers) {
    if (providers.includes('google')) return 'google';
    if (providers.includes('email')) return 'email';
  }

  const provider = user.app_metadata?.provider;
  if (provider === 'google') return 'google';
  if (provider === 'email') return 'email';

  return 'unknown';
}

export function AuthProvider({ children }: { children: ReactNode }) {
  console.log('[AuthContext:AuthProvider] Starting initialization');
  const initial = getStoredSession();
  const initialTokenExpired = isTokenExpired(initial.session);
  
  console.log('[AuthContext:AuthProvider] initial:', {
    hasUser: !!initial.user,
    hasSession: !!initial.session,
    initialTokenExpired,
    userId: initial.user?.id,
    provider: initial.user?.app_metadata?.provider,
  });
  
  const hasOAuthHash = typeof window !== 'undefined' && 
    window.location.hash.includes('access_token') && 
    !window.location.hash.includes('type=recovery');
  
  console.log('[AuthContext:AuthProvider] hasOAuthHash:', hasOAuthHash, 'hash:', window.location.hash.substring(0, 50));
  
  // Always keep user/session from localStorage, even if the token is expired.
  // This prevents the user from appearing "logged out" on page load when the
  // token just needs a background refresh.  The expired token will be refreshed
  // silently in initializeAuth() below.
  const [user, setUser] = useState<User | null>(() => {
    console.log('[AuthContext:useState:user] initialTokenExpired:', initialTokenExpired, 'result:', initial.user ? 'user object' : 'null');
    return initial.user;
  });
  const [profile, setProfile] = useState<Profile | null>(null);
  const [session, setSession] = useState<Session | null>(() => {
    console.log('[AuthContext:useState:session] initialTokenExpired:', initialTokenExpired, 'result:', initial.session ? 'session object' : 'null');
    return initial.session;
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  
  // authReady is true immediately when we have a stored session (even if the token
  // is expired — we keep the user logged in and refresh silently).  Only defer
  // readiness when an OAuth callback hash needs processing first.
  const [authReady, setAuthReady] = useState(!hasOAuthHash);

  console.log('[AuthContext:AuthProvider] Initial state:', {
    user: !!initial.user,
    session: !!initial.session,
    authReady: !hasOAuthHash,
  });
  const [creatorFeaturesEnabled, setCreatorFeaturesEnabled] = useState(false);
  const profileFetchedRef = useRef(false);
  
  // Promise deduplication: store the active refresh promise
  const refreshingPromiseRef = useRef<Promise<Session | null> | null>(null);

  // Retry state for exponential backoff on refresh failure
  const refreshRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshRetryCountRef = useRef(0);
  const REFRESH_RETRY_DELAYS = [10_000, 30_000, 60_000, 120_000, 300_000]; // 10s, 30s, 1m, 2m, 5m

  // Schedule a retry of refreshSession with exponential backoff
  const scheduleRefreshRetry = () => {
    if (refreshRetryTimerRef.current) return; // already scheduled
    const delay = REFRESH_RETRY_DELAYS[Math.min(refreshRetryCountRef.current, REFRESH_RETRY_DELAYS.length - 1)];
    refreshRetryCountRef.current++;
    console.log(`[AuthContext:scheduleRefreshRetry] Scheduling retry #${refreshRetryCountRef.current} in ${delay / 1000}s`);
    refreshRetryTimerRef.current = setTimeout(() => {
      refreshRetryTimerRef.current = null;
      refreshSession();
    }, delay);
  };

  // Refresh the access token using the refresh token
  const refreshSession = async (storedSession?: Session | null): Promise<Session | null> => {
    console.log('[AuthContext:refreshSession] Called, hasStoredSession:', !!storedSession, 'hasStateSession:', !!session);
    // 1. Deduplicate: if a refresh is running, return its promise
    if (refreshingPromiseRef.current) {
      console.log('[AuthContext:refreshSession] Refresh already in progress, returning existing promise');
      return refreshingPromiseRef.current;
    }

    // Always prefer localStorage over React state for the refresh token,
    // because React state may be stale (closure capture) while localStorage
    // is updated synchronously by saveSession() after each successful refresh.
    // Using the stale state causes refresh_token_already_used errors.
    const currentSession = storedSession || getStoredSession().session || session;
    
    console.log('[AuthContext:refreshSession] currentSession has refresh_token:', !!currentSession?.refresh_token);
    
    if (!currentSession?.refresh_token) {
      console.log('[AuthContext:refreshSession] No refresh_token available, returning null');
      return null;
    }

    // 2. Lock: Create a new promise for this refresh attempt
    refreshingPromiseRef.current = (async () => {
      try {
        console.log('[AuthContext:refreshSession] Calling Supabase refresh endpoint');
        const response = await fetch(`${supabaseUrl}/auth/v1/token?grant_type=refresh_token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': supabaseAnonKey,
          },
          body: JSON.stringify({
            refresh_token: currentSession.refresh_token,
          }),
        });

        console.log('[AuthContext:refreshSession] Response status:', response.status);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('Token refresh failed:', response.status, errorData);
          
          // Never clear session on refresh failure — only signOut() should do that.
          // Schedule a retry with exponential backoff instead.
          scheduleRefreshRetry();
          return null;
        }

        const data = await response.json();
        
        if (!data.access_token) {
          console.log('[AuthContext:refreshSession] No access_token in response');
          return null;
        }

        console.log('[AuthContext:refreshSession] Refresh successful, user:', data.user?.id);
        // Reset retry backoff on success
        refreshRetryCountRef.current = 0;
        if (refreshRetryTimerRef.current) {
          clearTimeout(refreshRetryTimerRef.current);
          refreshRetryTimerRef.current = null;
        }
        const newSession: Session = {
          access_token: data.access_token,
          refresh_token: data.refresh_token,
          expires_at: data.expires_at,
          expires_in: data.expires_in,
          token_type: data.token_type || 'bearer',
          user: data.user,
        };

        setUser(data.user);
        setSession(newSession);
        saveSession(newSession);

        return newSession;
      } catch (err) {
        console.error('Token refresh network error:', err);
        // Do NOT clear session on network errors — schedule a retry
        scheduleRefreshRetry();
        return null;
      } finally {
        // 3. Unlock: Clear the promise so future calls can run
        refreshingPromiseRef.current = null;
      }
    })();

    return refreshingPromiseRef.current;
  };

  const fetchProfile = async (userId: string, accessToken?: string) => {
    setProfileLoading(true);
    try {
      const { data, error } = await dbSelect<Profile>('profiles', {
        token: accessToken || session?.access_token,
        filters: { id: `eq.${userId}` },
        single: true,
      });

      if (error) {
        const errorMsg = error.message || '';
        if (errorMsg.includes('JWT expired') || errorMsg.includes('401')) {
          const newSession = await refreshSession();
          if (newSession) {
            const { data: retryData, error: retryError } = await dbSelect<Profile>('profiles', {
              token: newSession.access_token,
              filters: { id: `eq.${userId}` },
              single: true,
            });
            if (!retryError) {
              setProfile(retryData ?? null);
              return;
            }
          }
        }
        setProfile(null);
        return;
      }

      setProfile(data ?? null);
    } catch (err) {
      console.error('fetchProfile error:', err);
      setProfile(null);
    } finally {
      setProfileLoading(false);
    }
  };

  const fetchAppSettings = async (accessToken?: string) => {
    try {
      const { data, error } = await dbSelect<{ creator_features_enabled: boolean }>('app_settings', {
        token: accessToken || session?.access_token || supabaseAnonKey,
        filters: { id: 'eq.global' },
        single: true,
      });
      if (!error && data) {
        setCreatorFeaturesEnabled(data.creator_features_enabled);
      }
    } catch (err) {
      console.error('fetchAppSettings error:', err);
    }
  };

  const handleOAuthCallback = () => {
    console.log('[AuthContext:handleOAuthCallback] Called');
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const accessToken = hashParams.get('access_token');
    const refreshToken = hashParams.get('refresh_token');
    const type = hashParams.get('type');
    
    console.log('[AuthContext:handleOAuthCallback] params:', { hasAccessToken: !!accessToken, type });
    
    if (accessToken && type !== 'recovery') {
      try {
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
        console.log('[AuthContext:handleOAuthCallback] Parsed payload:', { sub: payload.sub, email: payload.email });
        const newUser: User = {
          id: payload.sub,
          email: payload.email,
          app_metadata: payload.app_metadata || {},
          user_metadata: payload.user_metadata || {},
        };
        
        const expiresAt = payload.exp;
        const newSession: Session = {
          access_token: accessToken,
          refresh_token: refreshToken || undefined,
          user: newUser,
          token_type: 'bearer',
          expires_at: expiresAt,
        };
        
        setUser(newUser);
        setSession(newSession);
        saveSession(newSession);
        
        profileFetchedRef.current = true;
        fetchProfile(newUser.id, accessToken);
        fetchAppSettings(accessToken);
        
        let oauthReturnTo: string | null = null;
        try { oauthReturnTo = localStorage.getItem('oauth_return_to'); } catch (_) { /* ignore */ }
        
        console.log('[AuthContext:handleOAuthCallback] oauthReturnTo:', oauthReturnTo);
        
        if (oauthReturnTo) {
          try { localStorage.removeItem('oauth_return_to'); } catch (_) { /* ignore */ }
          window.location.replace(oauthReturnTo);
          return true;
        }
        
        setAuthReady(true);
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
        return true;
      } catch (e) {
        console.error('Error parsing OAuth callback token:', e);
        setAuthReady(true);
      }
    }
    return false;
  };

  useEffect(() => {
    const initializeAuth = async () => {
      console.log('[AuthContext:initializeAuth] Starting, initial state:', {
        hasInitialUser: !!initial.user,
        hasInitialSession: !!initial.session,
        initialTokenExpired,
      });
      
      try {
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const type = hashParams.get('type');
        const accessToken = hashParams.get('access_token');
        
        console.log('[AuthContext:initializeAuth] hash check:', { 
          hasAccessToken: !!accessToken, 
          type,
          hash: window.location.hash.substring(0, 50)
        });
        
        if (accessToken && type !== 'recovery') {
          console.log('[AuthContext:initializeAuth] Handling OAuth callback');
          handleOAuthCallback();
          return;
        }
        
        if (type === 'recovery' && accessToken) {
          console.log('[AuthContext:initializeAuth] Handling password recovery');
          setIsPasswordRecovery(true);
          try {
            const payload = JSON.parse(atob(accessToken.split('.')[1]));
            const newUser: User = { id: payload.sub, email: payload.email };
            const newSession: Session = {
              access_token: accessToken,
              refresh_token: hashParams.get('refresh_token') || undefined,
              user: newUser,
              token_type: 'bearer',
            };
            
            setUser(newUser);
            setSession(newSession);
            saveSession(newSession);
            window.history.replaceState(null, '', window.location.pathname);
          } catch (e) {
            console.error('Error parsing recovery token:', e);
          }
          setAuthReady(true);
          return;
        }

        if (initial.user && initial.session && !profileFetchedRef.current) {
          console.log('[AuthContext:initializeAuth] Have initial session, fetching profile');
          profileFetchedRef.current = true;
          if (initialTokenExpired) {
            console.log('[AuthContext:initializeAuth] Token expired, attempting background refresh');
            // Refresh silently — user/session are already in state from localStorage
            // so the UI stays rendered while we get a fresh token.
            try {
              const newSession = await refreshSession(initial.session);
              console.log('[AuthContext:initializeAuth] Refresh result:', { success: !!newSession });
              if (newSession) {
                fetchProfile(newSession.user.id, newSession.access_token);
                fetchAppSettings(newSession.access_token);
              } else {
                // Refresh failed (e.g. offline) — still fetch profile with stale token;
                // fetchProfile will retry with a refreshed token on 401.
                fetchProfile(initial.user.id, initial.session.access_token);
                fetchAppSettings(initial.session.access_token);
              }
            } catch (e) {
              console.error('Session refresh error:', e);
              // Still try to fetch profile — fetchProfile handles 401 retry
              fetchProfile(initial.user.id, initial.session.access_token);
              fetchAppSettings(initial.session.access_token);
            }
          } else {
            console.log('[AuthContext:initializeAuth] Token valid, fetching profile directly');
            fetchProfile(initial.user.id, initial.session.access_token);
            fetchAppSettings(initial.session.access_token);
          }
        } else {
          console.log('[AuthContext:initializeAuth] No initial session or already fetched', {
            hasUser: !!initial.user,
            hasSession: !!initial.session,
            alreadyFetched: profileFetchedRef.current,
          });
        }
        
        // Auth initialization is complete
        console.log('[AuthContext:initializeAuth] Setting authReady to true');
        setAuthReady(true);
      } catch (e) {
        console.error('Auth initialization error:', e);
        setAuthReady(true);
      }
    };

    initializeAuth();
    
    // Expose debug function globally
    (window as any).__debugAuth = () => {
      const stored = getStoredSession();
      return {
        reactState: { user: !!user, session: !!session, authReady },
        localStorage: stored,
        tokenExpired: stored.session ? isTokenExpired(stored.session) : null,
      };
    };
  }, []);

  // Debug: Track user/session state changes
  useEffect(() => {
    console.log('[AuthContext:stateChange] user changed:', user ? `id:${user.id}` : 'null');
  }, [user]);
  
  useEffect(() => {
    console.log('[AuthContext:stateChange] session changed:', session ? `token:${session.access_token?.substring(0, 10)}...` : 'null');
  }, [session]);
  
  useEffect(() => {
    console.log('[AuthContext:stateChange] authReady changed:', authReady);
  }, [authReady]);

  useEffect(() => {
    if (!session) return;
    const checkAndRefresh = () => {
      if (isTokenExpired(session)) {
        refreshSession();
      }
    };
    const interval = setInterval(checkAndRefresh, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [session]);

  // Refresh token when tab becomes visible again (handles browser throttling setInterval in background tabs)
  useEffect(() => {
    if (!session) return;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[AuthContext:visibilitychange] Tab became visible, checking token');
        if (isTokenExpired(session)) {
          refreshSession();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [session]);

  // Refresh token when browser comes back online after being offline
  useEffect(() => {
    if (!session) return;
    const handleOnline = () => {
      console.log('[AuthContext:online] Browser is back online, checking token');
      if (isTokenExpired(session)) {
        refreshSession();
      }
    };
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [session]);

  const signUp = async (email: string, password: string, displayName?: string, country?: string): Promise<{ error: AuthError | null }> => {
    try {
      const response = await fetch(`${supabaseUrl}/auth/v1/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'apikey': supabaseAnonKey },
        body: JSON.stringify({
          email, password,
          data: { display_name: displayName || email.split('@')[0], country: country || null },
        }),
      });
      const data = await response.json();
      if (!response.ok) return { error: { message: data.error_description || data.msg || 'Sign up failed', status: response.status } };

      if (data.access_token && data.user) {
        const newSession: Session = {
          access_token: data.access_token,
          refresh_token: data.refresh_token,
          expires_at: data.expires_at,
          expires_in: data.expires_in,
          token_type: data.token_type || 'bearer',
          user: data.user,
        };
        setUser(data.user);
        setSession(newSession);
        saveSession(newSession);
        profileFetchedRef.current = true;
        fetchProfile(data.user.id, data.access_token);
        fetchAppSettings(data.access_token);
      }
      return { error: null };
    } catch (err) {
      return { error: { message: err instanceof Error ? err.message : 'Sign up failed' } };
    }
  };

  const signIn = async (email: string, password: string): Promise<{ error: AuthError | null }> => {
    try {
      const response = await fetch(`${supabaseUrl}/auth/v1/token?grant_type=password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'apikey': supabaseAnonKey },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      if (!response.ok) return { error: { message: data.error_description || data.msg || 'Sign in failed', status: response.status } };

      const newSession: Session = {
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        expires_at: data.expires_at,
        expires_in: data.expires_in,
        token_type: data.token_type || 'bearer',
        user: data.user,
      };
      setUser(data.user);
      setSession(newSession);
      saveSession(newSession);
      profileFetchedRef.current = true;
      fetchProfile(data.user.id, data.access_token);
      fetchAppSettings(data.access_token);
      return { error: null };
    } catch (err) {
      return { error: { message: err instanceof Error ? err.message : 'Sign in failed' } };
    }
  };

  const signInWithGoogle = async (redirectTo?: string): Promise<{ error: AuthError | null }> => {
    try {
      if (redirectTo && redirectTo !== '/dashboard') {
        try { localStorage.setItem('oauth_return_to', redirectTo); } catch (_) { /* ignore */ }
      } else {
        try { localStorage.removeItem('oauth_return_to'); } catch (_) { /* ignore */ }
      }
      const redirectUrl = `${window.location.origin}/`;
      const params = new URLSearchParams({ provider: 'google', redirect_to: redirectUrl, prompt: 'select_account' });
      window.location.href = `${supabaseUrl}/auth/v1/authorize?${params.toString()}`;
      return { error: null };
    } catch (err) {
      return { error: { message: err instanceof Error ? err.message : 'Google sign in failed' } };
    }
  };

  const signOut = async (): Promise<void> => {
    console.log('[AuthContext:signOut] Signing out, hasToken:', !!session?.access_token);
    const token = session?.access_token;
    // Clear any pending refresh retry
    if (refreshRetryTimerRef.current) {
      clearTimeout(refreshRetryTimerRef.current);
      refreshRetryTimerRef.current = null;
    }
    refreshRetryCountRef.current = 0;
    setUser(null);
    setProfile(null);
    setSession(null);
    profileFetchedRef.current = false;
    clearSession();
    if (token) {
      fetch(`${supabaseUrl}/auth/v1/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${token}`,
        },
      }).catch(e => console.error('Logout API error:', e));
    }
  };

  const updateProfile = async (updates: any): Promise<{ error: Error | null }> => {
    if (!user || !session) return { error: new Error('No user logged in') };
    try {
      const freshSession = await refreshSession();
      const tokenToUse = freshSession?.access_token || session.access_token;
      const { error } = await dbUpdate('profiles', updates, { id: `eq.${user.id}` }, { token: tokenToUse, returning: false });
      if (error) return { error: new Error(error.message) };
      await fetchProfile(user.id, tokenToUse);
      return { error: null };
    } catch (err) {
      return { error: err instanceof Error ? err : new Error('Unknown error') };
    }
  };

  const refreshProfile = async (): Promise<void> => {
    if (user && session) await fetchProfile(user.id, session.access_token);
  };

  const refreshAppSettings = async (): Promise<void> => {
    await fetchAppSettings(session?.access_token);
  };

  const resetPassword = async (email: string): Promise<{ error: AuthError | null }> => {
    try {
      const response = await fetch(`${supabaseUrl}/auth/v1/recover`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'apikey': supabaseAnonKey },
        body: JSON.stringify({ email, redirect_to: `${window.location.origin}/reset-password` }),
      });
      if (!response.ok) {
        const data = await response.json();
        return { error: { message: data.error_description || 'Password reset failed', status: response.status } };
      }
      return { error: null };
    } catch (err) {
      return { error: { message: err instanceof Error ? err.message : 'Password reset failed' } };
    }
  };

  const updatePassword = async (newPassword: string): Promise<{ error: AuthError | null }> => {
    if (!session?.access_token) return { error: { message: 'No session' } };
    try {
      const response = await fetch(`${supabaseUrl}/auth/v1/user`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ password: newPassword }),
      });
      if (!response.ok) {
        const data = await response.json();
        return { error: { message: data.error_description || 'Password update failed', status: response.status } };
      }
      setIsPasswordRecovery(false);
      return { error: null };
    } catch (err) {
      return { error: { message: err instanceof Error ? err.message : 'Password update failed' } };
    }
  };

  const getAuthProvider = (): AuthProvider => determineAuthProvider(user);
  const isGoogleAccount = (): boolean => getAuthProvider() === 'google';
  const isEmailAccount = (): boolean => getAuthProvider() === 'email';

  // Get a fresh access token, ONLY refreshing if actually expired
  const ensureFreshToken = async (
    options?: { forceRefresh?: boolean }
  ): Promise<string | null> => {
    if (!session) return null;
    
    if (options?.forceRefresh || isTokenExpired(session)) {
      const freshSession = await refreshSession();
      // If refresh failed (returned null), return null (forcing re-login or error)
      // BUT if it failed due to network error, it might return null but NOT clear session.
      // In that case, we can't do much but try again later.
      if (!freshSession?.access_token) {
        return null;
      }
      return freshSession.access_token;
    }
    
    return session.access_token;
  };

  const subscriptionTier: SubscriptionTier = (profile?.subscription_tier as SubscriptionTier) || 'free';
  const isPaidUser = subscriptionTier !== 'free';
  const isProUser = subscriptionTier === 'standard' || subscriptionTier === 'premium';
  const isPremiumUser = subscriptionTier === 'premium';
  const hasAds = subscriptionTier === 'free' || subscriptionTier === 'standard_with_ads';
  const subscriptionExpiresAt = profile?.subscription_expires_at ? new Date(profile.subscription_expires_at) : null;
  const storageLimitBytes = STORAGE_LIMITS[subscriptionTier];

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        session,
        loading: false,
        profileLoading,
        authReady,
        isAdmin: profile?.email === ADMIN_EMAIL,
        canUseClaudeCode: !!user,
        creatorFeaturesEnabled,
        isPasswordRecovery,
        subscriptionTier,
        isPaidUser,
        isProUser,
        isPremiumUser,
        hasAds,
        subscriptionExpiresAt,
        storageLimitBytes,
        signUp,
        signIn,
        signInWithGoogle,
        signOut,
        updateProfile,
        refreshProfile,
        refreshAppSettings,
        resetPassword,
        updatePassword,
        getAuthProvider,
        isGoogleAccount,
        isEmailAccount,
        ensureFreshToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}